import { FC, useState } from 'react';
import styled from 'styled-components';
import { apiUnlinkOwnWrestler, apiAdminUnlinkWrestler } from '../ui_components/ApiList';
import ModalConfirmAssignWrestlerToUser from '../wrestlers_components/ModalConfirmAssignWrestlerToUser';
import ModalAdminAssignWrestlerToUser from '../wrestlers_components/ModalAdminAssignWrestlerToUser';
import type { User, WrestlerWithStats } from '../../types';

const ActionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
`;

const ActionButton = styled.button`
  cursor: pointer;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-weight: 500;
  font-size: 14px;
  text-align: center;
  border: none;
  color: white;
  transition: all 0.15s ease-in-out;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.btn-select {
    background-color: #198754;
    &:hover:not(:disabled) {
      background-color: #157347;
    }
  }

  &.btn-unlink {
    background-color: #dc3545;
    &:hover:not(:disabled) {
      background-color: #bb2d3b;
    }
  }

  &.btn-admin-unlink {
    background-color: #fd7e14;
    &:hover:not(:disabled) {
      background-color: #e96b0a;
    }
  }

  &.btn-admin-assign {
    background-color: #0d6efd;
    &:hover:not(:disabled) {
      background-color: #0b5ed7;
    }
  }
`;

interface WrestlerActionsProps {
  user: User | null;
  wrestlerData: WrestlerWithStats;
  onWrestlerDataUpdate: () => void;
}

const WrestlerActions: FC<WrestlerActionsProps> = ({
  user,
  wrestlerData,
  onWrestlerDataUpdate,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdminAssignModalOpen, setIsAdminAssignModalOpen] = useState(false);
  const [unlinkLoading, setUnlinkLoading] = useState(false);
  const [adminUnlinkLoading, setAdminUnlinkLoading] = useState(false);

  const wrestlerUser = wrestlerData.user;
  const isLoggedIn = !!user;
  const isOwnWrestler = isLoggedIn && wrestlerUser?.id === user?.id;
  const isAdmin = isLoggedIn && !!(user?.is_admin || user?.is_super_admin || user?.is_founder);

  const handleSelectWrestler = (): void => {
    setIsModalOpen(true);
  };

  const handleModalClose = (reloadData?: boolean): void => {
    setIsModalOpen(false);
    if (reloadData) {
      onWrestlerDataUpdate();
    }
  };

  const handleUnlinkSelf = async (): Promise<void> => {
    setUnlinkLoading(true);
    try {
      await apiUnlinkOwnWrestler(wrestlerData.id);
      onWrestlerDataUpdate();
    } catch (error) {
      console.error('Errore durante la disassociazione:', error);
    } finally {
      setUnlinkLoading(false);
    }
  };

  const handleAdminUnlink = async (): Promise<void> => {
    if (!window.confirm('Sei sicuro di voler disassociare questo utente dal wrestler?')) return;
    setAdminUnlinkLoading(true);
    try {
      const res = await apiAdminUnlinkWrestler(wrestlerData.id) as any;
      // 409 — wrestler già non associato, refresh comunque
      if (res?.errors?.accessError) {
        console.warn('Admin unlink: non autorizzato');
      } else {
        onWrestlerDataUpdate();
      }
    } catch (error) {
      console.error('Errore durante la disassociazione admin:', error);
    } finally {
      setAdminUnlinkLoading(false);
    }
  };

  if (!isLoggedIn) return null;

  return (
    <ActionsContainer>
      {/* Wrestler non assegnato → pulsante "Seleziona Wrestler" */}
      {!wrestlerUser && (
        <ActionButton className="btn-select" onClick={handleSelectWrestler}>
          Associati Wrestler
        </ActionButton>
      )}

      {/* Admin: assegna wrestler a uno specifico utente */}
      {isAdmin && !wrestlerUser && (
        <ActionButton className="btn-admin-assign" onClick={() => setIsAdminAssignModalOpen(true)}>
          Assegna a utente
        </ActionButton>
      )}

      {/* Wrestler assegnato a me → pulsante "Disassocia" */}
      {isOwnWrestler && (
        <ActionButton
          className="btn-unlink"
          disabled={unlinkLoading}
          onClick={handleUnlinkSelf}
        >
          {unlinkLoading ? 'Caricamento...' : 'Disassocia'}
        </ActionButton>
      )}

      {/* Admin: pulsante per disassociare qualsiasi utente */}
      {isAdmin && wrestlerUser && (
        <ActionButton
          className="btn-admin-unlink"
          disabled={adminUnlinkLoading}
          onClick={handleAdminUnlink}
        >
          {adminUnlinkLoading ? 'Caricamento...' : 'Disassocia utente'}
        </ActionButton>
      )}

      {/* Modale per selezionare wrestler */}
      {isModalOpen && user && (
        <ModalConfirmAssignWrestlerToUser
          user={user}
          wrestlerData={wrestlerData}
          handleOpenModal={handleModalClose}
        />
      )}

      {/* Modale admin per assegnare wrestler a un utente specifico */}
      {isAdminAssignModalOpen && (
        <ModalAdminAssignWrestlerToUser
          wrestlerData={wrestlerData}
          onClose={(reload) => {
            setIsAdminAssignModalOpen(false);
            if (reload) onWrestlerDataUpdate();
          }}
        />
      )}
    </ActionsContainer>
  );
};

export default WrestlerActions;
